import { useEffect, useState } from 'react';
import Joyride, { CallBackProps, EVENTS, STATUS, Step } from 'react-joyride';
import { useTranslation } from 'react-i18next';

import { useAppDispatch, useAppSelector } from 'shared/lib/redux';
import { useEscapeKey } from 'shared/lib/utils';

import {
  showWalkthroughStep,
  startWalkthrough,
  useWalkthroughConfig,
} from '../model';
import {
  getWalkthroughSteps,
  WT_DEFAULT_SCROLL_OFFSET,
  WT_PROGRAMS_SCROLL_OFFSET,
} from '../constants';
import { JoyRideActionTypes, WalkthroughType } from '../types';
import { useWalkthroughCompletion } from '../lib';

import { WalkthroughTooltip } from './walkthrough-tooltip';

export const Walkthrough = () => {
  const { t } = useTranslation();

  const {
    shortWalkthroughSteps,
    fullWalkthroughSteps,
    fullWalkthroughStepsWithoutPrograms,
    shortWalkthroughStepsWithoutPrograms,
  } = getWalkthroughSteps(t);
  const [walkthroughSteps, setWalkthroughSteps] = useState<Step[] | null>(null);
  const [scrollOffset, setScrollOffset] = useState(WT_PROGRAMS_SCROLL_OFFSET);

  const { run, stepIndex } = useAppSelector((state) => state.walkthrough);

  const dispatch = useAppDispatch();

  const walkthroughConfig = useWalkthroughConfig();
  const completeWalkthrough = useWalkthroughCompletion();

  useEscapeKey(completeWalkthrough);

  const isNextStep = (type: string, status: string, action: string) => {
    return (
      (type === EVENTS.STEP_AFTER || type === EVENTS.TARGET_NOT_FOUND) &&
      action === JoyRideActionTypes.NEXT &&
      status === STATUS.RUNNING
    );
  };

  const isPreviousStep = (type: string, status: string, action: string) => {
    return (
      (type === EVENTS.STEP_AFTER || type === EVENTS.TARGET_NOT_FOUND) &&
      action === JoyRideActionTypes.PREV &&
      status === STATUS.RUNNING
    );
  };

  const isTourEnd = (type: string) => type === EVENTS.TOUR_END;

  const handleJoyrideCallback = ({
    index,
    type,
    status,
    action,
  }: CallBackProps) => {
    switch (true) {
      case isNextStep(type, status, action):
        setScrollOffset(WT_DEFAULT_SCROLL_OFFSET);
        dispatch(showWalkthroughStep(index + 1));
        break;
      case isPreviousStep(type, status, action):
        if (index - 1 === 0) {
          setScrollOffset(WT_PROGRAMS_SCROLL_OFFSET);
        }
        dispatch(showWalkthroughStep(index - 1));
        break;
      case isTourEnd(type):
        completeWalkthrough();
        break;
    }
  };

  useEffect(() => {
    if (!walkthroughConfig) return;

    switch (walkthroughConfig.type) {
      case WalkthroughType.SHORT_NO_PROGRAMS:
        setWalkthroughSteps(shortWalkthroughStepsWithoutPrograms);
        break;
      case WalkthroughType.SHORT:
        setWalkthroughSteps(shortWalkthroughSteps);
        break;
      case WalkthroughType.FULL_NO_PROGRAMS:
        setWalkthroughSteps(fullWalkthroughStepsWithoutPrograms);
        break;
      case WalkthroughType.FULL:
        setWalkthroughSteps(fullWalkthroughSteps);
        break;
    }
  }, [walkthroughConfig]);

  useEffect(() => {
    dispatch(startWalkthrough());
  }, [dispatch]);

  // dispatch 'resize' event to force highlighted area recalculation on screen orientation change
  useEffect(() => {
    const handleOrientationChange = () => {
      dispatchEvent(new Event('resize'));
    };

    window.screen.orientation.addEventListener(
      'change',
      handleOrientationChange,
    );

    return () => {
      window.screen.orientation.removeEventListener(
        'change',
        handleOrientationChange,
      );
    };
  }, []);

  if (!walkthroughSteps) return null;

  return (
    <Joyride
      scrollOffset={scrollOffset}
      continuous
      disableCloseOnEsc
      disableOverlayClose
      steps={walkthroughSteps}
      run={run}
      stepIndex={stepIndex}
      callback={handleJoyrideCallback}
      tooltipComponent={WalkthroughTooltip}
    />
  );
};
